import React, { useState, useEffect, useRef } from 'react';
import {
   Box,
   Paper,
   Typography,
   TextField,
   IconButton,
   Avatar,
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
   Divider,
   Chip,
   Badge,
   InputAdornment,
   Menu,
   MenuItem,
   CircularProgress,
   Alert,
} from '@mui/material';
import {
   Send as SendIcon,
   EmojiEmotions as EmojiIcon,
   MoreVert as MoreIcon,
   Edit as EditIcon,
   Delete as DeleteIcon,
   Reply as ReplyIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import webSocketService, { ChatMessage, ChatRoom } from '../../services/WebSocketService';
import { useUser } from '../../contexts/UserContext';

interface Props {
   room: ChatRoom;
   onBack?: () => void;
}

const CustomChatView: React.FC<Props> = ({ room, onBack }) => {
   const { currentUser } = useUser();
   const [messages, setMessages] = useState<ChatMessage[]>([]);
   const [newMessage, setNewMessage] = useState('');
   const [loading, setLoading] = useState(true);
   const [sending, setSending] = useState(false);
   const [typingUsers, setTypingUsers] = useState<string[]>([]);
   const [contextMenu, setContextMenu] = useState<{
      mouseX: number;
      mouseY: number;
      message: ChatMessage;
   } | null>(null);
   const [editingMessage, setEditingMessage] = useState<string | null>(null);
   const [editText, setEditText] = useState('');
   const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
   const [error, setError] = useState<string | null>(null);

   const messagesEndRef = useRef<HTMLDivElement>(null);
   const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
   const messageInputRef = useRef<HTMLInputElement>(null);

   // Auto-scroll to bottom when new messages arrive
   const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
   };

   // Load initial messages
   useEffect(() => {
      const loadMessages = async () => {
         setLoading(true);
         try {
            const roomMessages = await webSocketService.getRoomMessages(room.id);
            setMessages(roomMessages);

            // Join the room
            await webSocketService.joinRoom(room.id);
         } catch (err) {
            console.error('Error loading messages:', err);
            setError('Failed to load messages');
         } finally {
            setLoading(false);
         }
      };

      loadMessages();
   }, [room.id]);

   // Setup real-time message listeners
   useEffect(() => {
      const unsubscribeMessage = webSocketService.on('new-message', (message: ChatMessage) => {
         if (message.roomId === room.id) {
            setMessages((prev) => [...prev, message]);
         }
      });

      const unsubscribeTyping = webSocketService.on('user-typing', (data) => {
         if (data.roomId === room.id && data.userId !== currentUser?.uid) {
            setTypingUsers((prev) => {
               if (!prev.includes(data.username)) {
                  return [...prev, data.username];
               }
               return prev;
            });

            // Clear typing indicator after 3 seconds
            setTimeout(() => {
               setTypingUsers((prev) => prev.filter((u) => u !== data.username));
            }, 3000);
         }
      });

      return () => {
         // Leave room when component unmounts
         webSocketService.leaveRoom(room.id);
      };
   }, [room.id, currentUser?.uid]);

   // Auto-scroll when messages change
   useEffect(() => {
      scrollToBottom();
   }, [messages]);

   const handleSendMessage = async () => {
      if (!newMessage.trim() || sending) return;

      setSending(true);
      try {
         if (editingMessage) {
            // Edit existing message
            await webSocketService.editMessage(editingMessage, newMessage);
            setEditingMessage(null);
         } else {
            // Send new message
            await webSocketService.sendMessage(room.id, newMessage);
         }

         setNewMessage('');
         setReplyingTo(null);
         messageInputRef.current?.focus();
      } catch (err) {
         console.error('Error sending message:', err);
         setError('Failed to send message');
      } finally {
         setSending(false);
      }
   };

   const handleKeyPress = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
         event.preventDefault();
         handleSendMessage();
      }
   };

   const handleTyping = () => {
      webSocketService.startTyping(room.id);

      // Clear previous timeout
      if (typingTimeoutRef.current) {
         clearTimeout(typingTimeoutRef.current);
      }

      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
         webSocketService.stopTyping(room.id);
      }, 2000);
   };

   const handleContextMenu = (event: React.MouseEvent, message: ChatMessage) => {
      event.preventDefault();
      setContextMenu({
         mouseX: event.clientX + 2,
         mouseY: event.clientY - 6,
         message,
      });
   };

   const handleCloseContextMenu = () => {
      setContextMenu(null);
   };

   const handleEditMessage = (message: ChatMessage) => {
      setEditingMessage(message.id);
      setEditText(message.text);
      setNewMessage(message.text);
      handleCloseContextMenu();
      messageInputRef.current?.focus();
   };

   const handleDeleteMessage = (message: ChatMessage) => {
      if (window.confirm('Are you sure you want to delete this message?')) {
         webSocketService.deleteMessage(message.id);
      }
      handleCloseContextMenu();
   };

   const handleReplyToMessage = (message: ChatMessage) => {
      setReplyingTo(message);
      handleCloseContextMenu();
      messageInputRef.current?.focus();
   };

   const handleAddReaction = (message: ChatMessage, emoji: string) => {
      webSocketService.reactToMessage(message.id, emoji);
      handleCloseContextMenu();
   };

   const cancelEdit = () => {
      setEditingMessage(null);
      setEditText('');
      setNewMessage('');
   };

   const cancelReply = () => {
      setReplyingTo(null);
   };

   const formatMessageTime = (timestamp: Date) => {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
   };

   const isMyMessage = (message: ChatMessage) => {
      return message.senderId === currentUser?.uid;
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
         </Box>
      );
   }

   return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
         {/* Header */}
         <Box
            sx={{
               p: 2,
               borderBottom: '1px solid',
               borderColor: 'divider',
               bgcolor: 'background.paper',
            }}
         >
            <Typography variant='h6'>{room.name}</Typography>
            <Typography variant='body2' color='text.secondary'>
               {room.members.length} members
            </Typography>
         </Box>

         {error && (
            <Alert severity='error' sx={{ m: 1 }}>
               {error}
            </Alert>
         )}

         {/* Messages */}
         <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
            <List>
               {messages.map((message, index) => (
                  <ListItem
                     key={message.id}
                     sx={{
                        flexDirection: 'column',
                        alignItems: isMyMessage(message) ? 'flex-end' : 'flex-start',
                        py: 1,
                     }}
                     onContextMenu={(e) => handleContextMenu(e, message)}
                  >
                     <Box
                        sx={{
                           display: 'flex',
                           alignItems: 'flex-end',
                           maxWidth: '70%',
                           flexDirection: isMyMessage(message) ? 'row-reverse' : 'row',
                        }}
                     >
                        {!isMyMessage(message) && (
                           <Avatar
                              src={message.senderName} // Using senderName as fallback
                              alt={message.senderName}
                              sx={{ width: 32, height: 32, mr: 1 }}
                           >
                              {message.senderName?.[0]?.toUpperCase()}
                           </Avatar>
                        )}

                        <Paper
                           variant='outlined'
                           sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: isMyMessage(message) ? 'primary.light' : 'background.paper',
                              color: isMyMessage(message) ? 'primary.contrastText' : 'text.primary',
                           }}
                        >
                           {!isMyMessage(message) && (
                              <Typography
                                 variant='caption'
                                 sx={{ fontWeight: 'bold', display: 'block' }}
                              >
                                 {message.senderName}
                              </Typography>
                           )}

                           <Typography variant='body1'>{message.text}</Typography>

                           {message.edited && (
                              <Typography
                                 variant='caption'
                                 color='text.secondary'
                                 sx={{ fontStyle: 'italic' }}
                              >
                                 (edited)
                              </Typography>
                           )}

                           {message.reactions && Object.keys(message.reactions).length > 0 && (
                              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                 {Object.entries(message.reactions).map(([emoji, users]) => (
                                    <Chip
                                       key={emoji}
                                       label={`${emoji} ${users.length}`}
                                       size='small'
                                       variant='outlined'
                                       sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                 ))}
                              </Box>
                           )}
                        </Paper>

                        {isMyMessage(message) && (
                           <Avatar
                              src={message.senderName} // Using senderName as fallback
                              alt={message.senderName}
                              sx={{ width: 32, height: 32, ml: 1 }}
                           >
                              {message.senderName?.[0]?.toUpperCase()}
                           </Avatar>
                        )}
                     </Box>

                     <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{
                           mt: 0.5,
                           alignSelf: isMyMessage(message) ? 'flex-end' : 'flex-start',
                           mr: isMyMessage(message) ? 5 : 0,
                           ml: isMyMessage(message) ? 0 : 5,
                        }}
                     >
                        {formatMessageTime(message.timestamp)}
                     </Typography>
                  </ListItem>
               ))}

               {/* Typing indicator */}
               {typingUsers.length > 0 && (
                  <ListItem>
                     <ListItemText
                        primary={
                           <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ fontStyle: 'italic' }}
                           >
                              {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'}{' '}
                              typing...
                           </Typography>
                        }
                     />
                  </ListItem>
               )}

               <div ref={messagesEndRef} />
            </List>
         </Box>

         {/* Reply context in input */}
         {replyingTo && (
            <Box
               sx={{
                  p: 1,
                  bgcolor: 'action.hover',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
               }}
            >
               <Typography variant='body2' color='text.secondary'>
                  Replying to {replyingTo.senderName}: {replyingTo.text.substring(0, 50)}
                  ...
               </Typography>
               <IconButton size='small' onClick={cancelReply}>
                  ×
               </IconButton>
            </Box>
         )}

         {/* Edit mode indicator */}
         {editingMessage && (
            <Box
               sx={{
                  p: 1,
                  bgcolor: 'warning.light',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
               }}
            >
               <Typography variant='body2'>Editing message</Typography>
               <IconButton size='small' onClick={cancelEdit}>
                  ×
               </IconButton>
            </Box>
         )}

         {/* Message Input */}
         <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <TextField
               fullWidth
               multiline
               maxRows={4}
               value={newMessage}
               onChange={(e) => {
                  setNewMessage(e.target.value);
                  handleTyping();
               }}
               onKeyPress={handleKeyPress}
               placeholder={editingMessage ? 'Edit your message...' : 'Type a message...'}
               ref={messageInputRef}
               InputProps={{
                  endAdornment: (
                     <InputAdornment position='end'>
                        <IconButton
                           onClick={handleSendMessage}
                           disabled={!newMessage.trim() || sending}
                           color='primary'
                        >
                           {sending ? <CircularProgress size={20} /> : <SendIcon />}
                        </IconButton>
                     </InputAdornment>
                  ),
               }}
            />
         </Box>

         {/* Context Menu */}
         <Menu
            open={contextMenu !== null}
            onClose={handleCloseContextMenu}
            anchorReference='anchorPosition'
            anchorPosition={
               contextMenu !== null
                  ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                  : undefined
            }
         >
            <MenuItem onClick={() => handleReplyToMessage(contextMenu!.message)}>
               <ReplyIcon sx={{ mr: 1 }} />
               Reply
            </MenuItem>

            {contextMenu?.message.senderId === currentUser?.uid && (
               <MenuItem onClick={() => handleEditMessage(contextMenu!.message)}>
                  <EditIcon sx={{ mr: 1 }} />
                  Edit
               </MenuItem>
            )}

            {contextMenu?.message.senderId === currentUser?.uid && (
               <MenuItem onClick={() => handleDeleteMessage(contextMenu!.message)}>
                  <DeleteIcon sx={{ mr: 1 }} />
                  Delete
               </MenuItem>
            )}

            <Divider />

            {['👍', '❤️', '😂', '😮', '😢', '😡'].map((emoji) => (
               <MenuItem key={emoji} onClick={() => handleAddReaction(contextMenu!.message, emoji)}>
                  {emoji}
               </MenuItem>
            ))}
         </Menu>
      </Box>
   );
};

export default CustomChatView;
